import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Carousel, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCart } from '../contexts/CartContext';
import ProductMediaModal from '../components/ProductMediaModal';

const Products = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [mediaModalState, setMediaModalState] = useState({
    show: false,
    items: [],
    title: '',
    index: 0
  });
  const { user } = useAuth();
  const { t } = useLanguage();
  const { notify } = useNotification();
  const { refreshCart } = useCart();

  const activeCategoryFilters = useMemo(() => {
    const next = {};

    if (debouncedSearchTerm.trim()) {
      next.search = debouncedSearchTerm.trim();
    }
    if (selectedManufacturer) {
      next.manufacturer = selectedManufacturer;
    }
    if (inStockOnly) {
      next.inStock = 'true';
    }

    return next;
  }, [debouncedSearchTerm, selectedManufacturer, inStockOnly]);

  const activeProductFilters = useMemo(() => {
    const next = { ...activeCategoryFilters };

    if (selectedSubcategory) {
      next.subcategory = selectedSubcategory;
    } else if (selectedCategory && (activeCategoryFilters.search || activeCategoryFilters.manufacturer || activeCategoryFilters.inStock)) {
      next.category = selectedCategory;
    }

    return next;
  }, [activeCategoryFilters, selectedCategory, selectedSubcategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/api/categories', {
        params: activeCategoryFilters
      });
      const data = response.data;
      const normalizedCategories = Array.isArray(data) ? data : (data?.categories || []);
      setCategories(normalizedCategories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [activeCategoryFilters]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = user ? '/api/products/catalog/full' : '/api/products/catalog/public';
      const response = await api.get(endpoint, {
        params: activeProductFilters
      });
      const data = response.data;
      const normalizedProducts = Array.isArray(data) ? data : (data?.products || []);

      setAllProducts(normalizedProducts);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  }, [activeProductFilters, user, t]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  const createDefaultImageDataUri = (label = 'No Image') => {
    const safeLabel = String(label || 'No Image').replace(/[<>]/g, '');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420"><rect width="640" height="420" fill="#e9ecef"/><rect x="20" y="20" width="600" height="380" fill="none" stroke="#ced4da" stroke-width="3" stroke-dasharray="10 8"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6c757d" font-family="Segoe UI, Tahoma, sans-serif" font-size="30">${safeLabel}</text></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  const getDefaultImageDataUri = useCallback(() => createDefaultImageDataUri(t('products.noMedia')), [t]);

  const handleImageError = useCallback((event) => {
    const fallbackSrc = getDefaultImageDataUri();
    if (event?.currentTarget && event.currentTarget.src !== fallbackSrc) {
      event.currentTarget.onerror = null;
      event.currentTarget.src = fallbackSrc;
    }
  }, [getDefaultImageDataUri]);

  const getProductMainCategory = useCallback((product) => (
    product?.mainCategory?.name || product?.mainCategoryName || product?.main_category_name || product?.category?.mainCategory?.name || ''
  ), []);

  const getProductSubcategory = useCallback((product) => (
    product?.subcategory?.name || product?.subcategoryName || product?.subcategory_name || product?.category?.name || product?.categoryName || ''
  ), []);

  const getProductMainCategoryId = useCallback((product) => (
    product?.mainCategory?.id || product?.mainCategoryId || product?.main_category_id || product?.category?.mainCategory?.id || null
  ), []);

  const getProductSubcategoryId = useCallback((product) => (
    product?.subcategory?.id || product?.subcategoryId || product?.subcategory_id || product?.category?.id || product?.category_id || product?.categoryId || null
  ), []);

  const getApiOrigin = useCallback(() => {
    const baseUrl = api?.defaults?.baseURL;
    if (!baseUrl) {
      return '';
    }

    try {
      return new URL(baseUrl, window.location.origin).origin;
    } catch {
      return String(baseUrl)
        .replace(/\/api\/?$/, '')
        .replace(/\/$/, '');
    }
  }, []);

  const resolveAssetUrl = useCallback((rawUrl) => {
    if (!rawUrl) {
      return '';
    }

    const apiOrigin = getApiOrigin();

    try {
      const parsed = new URL(rawUrl, window.location.origin);
      if (parsed.pathname.startsWith('/api/files/') || parsed.pathname.startsWith('/uploads/')) {
        return parsed.toString();
      }
    } catch {
      // Keep fallback path handling for non-standard values.
    }

    if ((rawUrl.startsWith('/api/files') || rawUrl.startsWith('/uploads')) && apiOrigin) {
      return `${apiOrigin}${rawUrl}`;
    }

    if (rawUrl.startsWith('api/files/') && apiOrigin) {
      return `${apiOrigin}/${rawUrl}`;
    }

    if (rawUrl.startsWith('uploads/') && apiOrigin) {
      return `${apiOrigin}/${rawUrl}`;
    }

    return rawUrl;
  }, [getApiOrigin]);

  const getCategoryImageUrl = useCallback((category) => {
    const rawUrl = category?.image_url || category?.imageUrl || '';
    return resolveAssetUrl(rawUrl);
  }, [resolveAssetUrl]);

  const normalizeSubcategoryName = useCallback((mainCategoryName, subcategoryName) => {
    const main = String(mainCategoryName || '').trim();
    const sub = String(subcategoryName || '').trim();

    if (!main || !sub) {
      return sub;
    }

    const mainLower = main.toLowerCase();
    const subLower = sub.toLowerCase();
    const separators = [' - ', ' -> ', ': '];

    for (const separator of separators) {
      const prefix = `${mainLower}${separator}`;
      if (subLower.startsWith(prefix)) {
        return sub.slice(prefix.length).trim() || sub;
      }
    }

    return sub;
  }, []);

  const navigationFilteredProducts = useMemo(() => {
    const searchValue = debouncedSearchTerm.trim().toLowerCase();

    return allProducts.filter((product) => {
      const nameMatch = product.name?.toLowerCase().includes(searchValue);
      const mainCategoryMatch = getProductMainCategory(product).toLowerCase().includes(searchValue);
      const subcategoryMatch = getProductSubcategory(product).toLowerCase().includes(searchValue);
      const matchesSearch = !searchValue || nameMatch || mainCategoryMatch || subcategoryMatch;

      const matchesManufacturer = !selectedManufacturer || String(product.manufacturer || '').toLowerCase() === String(selectedManufacturer).toLowerCase();
      const matchesStock = !inStockOnly || Number(product.stock) > 0;

      return matchesSearch && matchesManufacturer && matchesStock;
    });
  }, [allProducts, debouncedSearchTerm, getProductMainCategory, getProductSubcategory, selectedManufacturer, inStockOnly]);

  const filteredProducts = useMemo(() => {
    return navigationFilteredProducts.filter((product) => (
      (!selectedCategory || String(getProductMainCategoryId(product) || getProductSubcategoryId(product) || '') === String(selectedCategory) || String(getProductMainCategoryId(product) || '') === String(selectedCategory)) &&
      (!selectedSubcategory || String(getProductSubcategoryId(product) || '') === String(selectedSubcategory))
    ));
  }, [navigationFilteredProducts, selectedCategory, selectedSubcategory, getProductMainCategoryId, getProductSubcategoryId]);

  const mergeSubcategoriesForDisplay = useCallback((mainCategory, children) => {
    const grouped = new Map();

    for (const child of children) {
      const normalizedName = normalizeSubcategoryName(mainCategory?.name, child?.name);
      const key = normalizedName.toLowerCase();
      if (!grouped.has(key)) {
        grouped.set(key, { name: normalizedName, items: [] });
      }
      grouped.get(key).items.push(child);
    }

    return Array.from(grouped.values()).map((group) => {
      const preferred = group.items
        .slice()
        .sort((left, right) => Number(right?.productCount || 0) - Number(left?.productCount || 0))[0] || group.items[0];
      const imageSource = group.items.find((item) => item?.imageUrl || item?.image_url) || preferred;
      const productCount = group.items.reduce((sum, item) => sum + Number(item?.productCount || 0), 0);

      return {
        ...preferred,
        name: group.name || preferred?.name,
        productCount,
        imageUrl: imageSource?.imageUrl || imageSource?.image_url || preferred?.imageUrl || preferred?.image_url || null,
        image_url: imageSource?.image_url || imageSource?.imageUrl || preferred?.image_url || preferred?.imageUrl || null
      };
    });
  }, [normalizeSubcategoryName]);

  const filteredCategories = useMemo(() => {
    return (Array.isArray(categories) ? categories : []).map((category) => {
      const rawChildren = Array.isArray(category.children) ? category.children : [];
      const children = mergeSubcategoriesForDisplay(category, rawChildren);
      const productCount = children.reduce((sum, child) => sum + Number(child?.productCount || 0), 0);

      return {
        ...category,
        children,
        productCount
      };
    });
  }, [categories, mergeSubcategoriesForDisplay]);

  const categoryProductCounts = useMemo(() => {
    return filteredCategories.reduce((acc, category) => {
      acc[String(category.id)] = Number(category.productCount || 0);

      return acc;
    }, {});
  }, [filteredCategories]);

  const availableSubcategories = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    const selected = filteredCategories.find((category) => String(category.id) === String(selectedCategory));
    if (!selected || !Array.isArray(selected.children)) {
      return [];
    }

    return selected.children;
  }, [filteredCategories, selectedCategory]);

  const selectedCategoryData = useMemo(() => {
    return filteredCategories.find((category) => String(category.id) === String(selectedCategory)) || null;
  }, [filteredCategories, selectedCategory]);

  const selectedSubcategoryData = useMemo(() => {
    return availableSubcategories.find((subcategory) => String(subcategory.id) === String(selectedSubcategory)) || null;
  }, [availableSubcategories, selectedSubcategory]);

  const manufacturers = useMemo(() => {
    return Array.from(
      new Set(
        allProducts
          .map((product) => String(product.manufacturer || '').trim())
          .filter(Boolean)
      )
    ).sort((left, right) => left.localeCompare(right));
  }, [allProducts]);

  // Returns an array of image URLs for a category, inheriting from subcategories
  // then product images when the category has no image of its own.
  const getCategoryImages = useCallback((category) => {
    const resolve = (raw) => resolveAssetUrl(raw || '');

    const ownUrl = resolve(category?.image_url || category?.imageUrl);
    if (ownUrl) return [ownUrl];

    const children = Array.isArray(category?.children) ? category.children : [];
    const subImages = [];
    for (const child of children) {
      const url = resolve(child?.image_url || child?.imageUrl);
      if (url && !subImages.includes(url)) subImages.push(url);
    }
    if (subImages.length > 0) return subImages.slice(0, 8);

    const catId = String(category?.id || '');
    const childIds = new Set(children.map((c) => String(c.id)));
    const seen = new Set();
    const productImages = [];
    for (const product of navigationFilteredProducts) {
      const pCatId = String(product.category_id || product.categoryId || '');
      if (pCatId === catId || childIds.has(pCatId)) {
        for (const img of (Array.isArray(product.images) ? product.images : [])) {
          const rawUrl = typeof img === 'string' ? img : (img?.imageUrl || img?.url || img?.path || '');
          const url = resolve(rawUrl);
          if (url && !seen.has(url)) {
            seen.add(url);
            productImages.push(url);
            if (productImages.length >= 8) break;
          }
        }
      }
      if (productImages.length >= 8) break;
    }
    return productImages;
  }, [resolveAssetUrl, navigationFilteredProducts]);

  // Returns an array of image URLs for a subcategory, falling back to product images.
  const getSubcategoryImages = useCallback((subcategory) => {
    const resolve = (raw) => resolveAssetUrl(raw || '');

    const ownUrl = resolve(subcategory?.image_url || subcategory?.imageUrl);
    if (ownUrl) return [ownUrl];

    const subcatId = String(subcategory?.id || '');
    const seen = new Set();
    const productImages = [];
    for (const product of navigationFilteredProducts) {
      const pCatId = String(product.category_id || product.categoryId || '');
      if (pCatId === subcatId) {
        for (const img of (Array.isArray(product.images) ? product.images : [])) {
          const rawUrl = typeof img === 'string' ? img : (img?.imageUrl || img?.url || img?.path || '');
          const url = resolve(rawUrl);
          if (url && !seen.has(url)) {
            seen.add(url);
            productImages.push(url);
            if (productImages.length >= 8) break;
          }
        }
      }
      if (productImages.length >= 8) break;
    }
    return productImages;
  }, [resolveAssetUrl, navigationFilteredProducts]);

  useEffect(() => {
    if (selectedCategory && !filteredCategories.some((category) => String(category.id) === String(selectedCategory))) {
      setSelectedCategory('');
    }
  }, [selectedCategory, filteredCategories]);

  useEffect(() => {
    if (selectedSubcategory && !availableSubcategories.some((child) => String(child.id) === String(selectedSubcategory))) {
      setSelectedSubcategory('');
    }
  }, [selectedSubcategory, availableSubcategories]);

  useEffect(() => {
    if (
      selectedManufacturer &&
      !manufacturers.some((manufacturer) => manufacturer.toLowerCase() === selectedManufacturer.toLowerCase())
    ) {
      setSelectedManufacturer('');
    }
  }, [selectedManufacturer, manufacturers]);

  

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedManufacturer('');
    setInStockOnly(false);
  };

  const searchActive = debouncedSearchTerm.trim().length > 0;
  const shouldShowProducts = searchActive || Boolean(selectedManufacturer) || inStockOnly || (Boolean(selectedCategory) && Boolean(selectedSubcategory));
  const visibleProducts = shouldShowProducts ? filteredProducts : [];
  const showInitialLoading = loading && categories.length === 0 && allProducts.length === 0;

  const addToCart = async (productId) => {
    try {
      await api.post('/api/cart/add', { productId, quantity: 1 });
      notify({ variant: 'success', message: t('common.success') });
      refreshCart();
    } catch (err) {
      notify({ variant: 'danger', message: t('common.error') });
      console.error('Failed to add to cart:', err);
    }
  };

  const getMediaUrl = (media) => {
    if (!media) return '';
    const rawUrl = typeof media === 'string'
      ? media
      : (media.imageUrl || media.url || media.path || '');
    return resolveAssetUrl(rawUrl);
  };

  const isVideo = (media) => {
    const url = getMediaUrl(media);
    return /\.(mp4|webm|ogg)$/i.test(url);
  };

  const openMediaModal = (product, startIndex) => {
    const items = Array.isArray(product?.images) ? product.images : [];
    if (items.length === 0) {
      return;
    }

    setMediaModalState({
      show: true,
      items,
      title: product?.name || t('products.title'),
      index: Number.isInteger(startIndex) ? startIndex : 0
    });
  };

  const closeMediaModal = () => {
    setMediaModalState((previous) => ({
      ...previous,
      show: false
    }));
  };

  const renderCategoryCardImage = useCallback((images, altText) => {
    const imgStyle = { height: '180px', objectFit: 'contain', backgroundColor: '#fff', marginTop: '6px' };
    const firstImage = (Array.isArray(images) ? images : []).filter(Boolean)[0];

    return (
      <Card.Img
        variant="top"
        src={firstImage || getDefaultImageDataUri()}
        onError={firstImage ? handleImageError : undefined}
        alt={altText}
        style={imgStyle}
      />
    );
  }, [getDefaultImageDataUri, handleImageError]);

  const renderProductMedia = (product) => {
    const mediaClassName = 'product-image';
    const fallbackSrc = getDefaultImageDataUri();

    if (product.images && product.images.length > 0) {
      return (
        <Carousel indicators={product.images.length > 1} controls={product.images.length > 1} interval={null}>
          {product.images.map((media, index) => {
            const mediaUrl = getMediaUrl(media);

            return (
              <Carousel.Item key={index}>
                {isVideo(media) ? (
                  <video className={`${mediaClassName} w-100`} controls preload="metadata">
                    <source src={mediaUrl} />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <Card.Img
                    variant="top"
                    src={mediaUrl}
                    className={mediaClassName}
                    alt={`${product.name} - ${index + 1}`}
                    onError={handleImageError}
                    role="button"
                    tabIndex={0}
                    onClick={() => openMediaModal(product, index)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openMediaModal(product, index);
                      }
                    }}
                  />
                )}
              </Carousel.Item>
            );
          })}
        </Carousel>
      );
    }

    return (
      <Card.Img
        variant="top"
        src={fallbackSrc}
        className={mediaClassName}
        alt={t('products.noMedia')}
      />
    );
  };

  const renderProductMeta = (product) => (
    <>
      <Card.Title>{product.name}</Card.Title>
      <Card.Text className="product-description flex-grow-1">
        {product.description}
      </Card.Text>

      {product.linkedProducts && product.linkedProducts.length > 0 && (
        <div className="linked-products-preview">
          <div className="linked-products-title">{t('products.linkedTitle')}</div>
          <div className="linked-products-chips">
            {product.linkedProducts.map((linked) => (
              <Link key={linked.id} to={`/products/${linked.id}`} className="linked-product-chip">
                {linked.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {user && (
        <>
          <div className="mb-3">
            <strong>{t('products.stock')}:</strong>{' '}
            {product.stock > 0 ? (
              <span className="stock-indicator in-stock" title={t('products.inStock')}>
                ✔ {t('products.inStock')}
              </span>
            ) : (
              <span className="stock-indicator out-of-stock" title={t('products.outOfStock')}>
                ✖ {t('products.outOfStock')}
              </span>
            )}
          </div>
        </>
      )}
    </>
  );

  const renderProductActions = (product) => (
    <div className="d-grid gap-2">
      <Button as={Link} to={`/products/${product.id}`} variant="outline-primary">
        {t('products.viewDetails')}
      </Button>
      {user && product.stock > 0 && (
        <Button variant="primary" onClick={() => addToCart(product.id)}>
          {t('products.addToCart')}
        </Button>
      )}
    </div>
  );

  const renderProductCard = (product) => (
    <Card className="product-card h-100">
      {renderProductMedia(product)}
      <Card.Body className="d-flex flex-column">
        {renderProductMeta(product)}
        {renderProductActions(product)}
      </Card.Body>
    </Card>
  );

  const renderCompactProductCard = (product) => {
    const primaryMedia = Array.isArray(product.images) ? product.images[0] : null;
    const mediaUrl = getMediaUrl(primaryMedia);
    const mainCategoryName = getProductMainCategory(product);
    const subcategoryName = getProductSubcategory(product);

    return (
      <Card className="product-card product-card-compact mb-3">
        <Row className="g-0 align-items-stretch">
          <Col md={3} lg={2}>
            {primaryMedia ? (
              isVideo(primaryMedia) ? (
                <video className="product-image product-image-compact w-100" controls preload="metadata">
                  <source src={mediaUrl} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <Card.Img
                  src={mediaUrl}
                  className="product-image product-image-compact"
                  alt={product.name}
                  onError={handleImageError}
                />
              )
            ) : (
              <Card.Img
                src={getDefaultImageDataUri()}
                className="product-image product-image-compact"
                alt={t('products.noMedia')}
              />
            )}
          </Col>
          <Col md={9} lg={10}>
            <Card.Body className="d-flex flex-column h-100">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-2">
                <div>
                  <Card.Title className="mb-1">{product.name}</Card.Title>
                  {(mainCategoryName || subcategoryName) && (
                    <div className="product-category-path">
                      {mainCategoryName && <span className="product-category-main">{mainCategoryName}</span>}
                      {mainCategoryName && subcategoryName && <span className="product-category-separator"> / </span>}
                      {subcategoryName && <span className="product-category-sub">{subcategoryName}</span>}
                    </div>
                  )}
                </div>
                {user && (
                  <div className="small text-muted">
                    <strong>{t('products.stock')}:</strong>{' '}
                    {product.stock > 0 ? t('products.inStock') : t('products.outOfStock')}
                  </div>
                )}
              </div>
              <Card.Text className="product-description product-description-compact">
                {product.description}
              </Card.Text>
              <div className="d-flex gap-2 flex-wrap mt-auto">
                <Button as={Link} to={`/products/${product.id}`} variant="outline-primary">
                  {t('products.viewDetails')}
                </Button>
                {user && product.stock > 0 && (
                  <Button variant="primary" onClick={() => addToCart(product.id)}>
                    {t('products.addToCart')}
                  </Button>
                )}
              </div>
            </Card.Body>
          </Col>
        </Row>
      </Card>
    );
  };

  if (showInitialLoading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('products.title')}</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {!user && (
        <Alert variant="info">
          {t('products.loginToSee')}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={12}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('products.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6}>
              <Form.Select
                value={selectedManufacturer}
                onChange={(e) => setSelectedManufacturer(e.target.value)}
              >
                <option value="">{t('products.allManufacturers')}</option>
                {(Array.isArray(manufacturers) ? manufacturers : []).map((mfg) => (
                  <option key={mfg} value={mfg}>{mfg}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Check
                type="checkbox"
                label={t('products.inStockOnly')}
                checked={inStockOnly}
                onChange={(e) => setInStockOnly(e.target.checked)}
              />
            </Col>
            <Col md={3}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={handleClearFilters}
              >
                {t('products.clearFilters')}
              </Button>
            </Col>
            <Col md={12}>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div className="text-muted small">
                  {t('products.showing')} {visibleProducts.length} {visibleProducts.length === 1 ? t('products.productSingular') : t('products.productPlural')}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          {!selectedCategory && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
                <div className="text-muted small">
                  {filteredCategories.length} {filteredCategories.length === 1 ? t('products.productSingular') : t('products.productPlural')} categories
                </div>
              </div>
              <Row xs={1} md={2} lg={3} className="g-3">
                {filteredCategories.map((category) => (
                  <Col key={category.id}>
                    <Card
                      className="h-100 border-0 shadow-sm bg-white"
                      role="button"
                      onClick={() => {
                        setSelectedCategory(String(category.id));
                        setSelectedSubcategory('');
                      }}
                    >
                      {renderCategoryCardImage(getCategoryImages(category), category.name)}
                      <Card.Body>
                        <Card.Title as="h3" className="mb-2 fs-3 fw-bold">
                          {category.name} ({categoryProductCounts[String(category.id)] || 0})
                        </Card.Title>
                        {category.description && <Card.Text className="text-muted small">{category.description}</Card.Text>}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {selectedCategory && !selectedSubcategory && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
                <div>
                  <Button variant="link" className="px-0 mb-2" onClick={() => setSelectedCategory('')}>
                    {t('products.backToCategories')}
                  </Button>
                  <h1 className="mb-1">{selectedCategoryData?.name || t('products.chooseSubcategory')}</h1>
                  <div className="text-muted">{t('products.chooseSubcategory')}</div>
                </div>
              </div>
              <Row xs={1} md={2} lg={3} className="g-3">
                {availableSubcategories.map((subcategory) => (
                  <Col key={subcategory.id}>
                    <Card
                      className="h-100 border-0 shadow-sm bg-white"
                      role="button"
                      onClick={() => setSelectedSubcategory(String(subcategory.id))}
                    >
                      {renderCategoryCardImage(getSubcategoryImages(subcategory), subcategory.name)}
                      <Card.Body>
                        <Card.Title as="h3" className="mb-2 fs-3 fw-bold">{subcategory.name}</Card.Title>
                        {subcategory.description && <Card.Text className="text-muted small">{subcategory.description}</Card.Text>}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {selectedCategory && selectedSubcategory && (
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <div>
                <Button variant="link" className="px-0 me-3" onClick={() => setSelectedSubcategory('')}>
                  {t('products.backToSubcategories')}
                </Button>
                <span className="products-main-category-highlight">{selectedCategoryData?.name}</span>
                <span className="text-muted mx-1"> / </span>
                <span className="text-muted">{selectedSubcategoryData?.name}</span>
              </div>
              <div className="text-muted small">
                {visibleProducts.length} {visibleProducts.length === 1 ? t('products.productSingular') : t('products.productPlural')}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {!shouldShowProducts && !loading && (
        <Alert variant="info">{t('products.selectCategorySubcategoryPrompt')}</Alert>
      )}

      {shouldShowProducts && filteredProducts.length === 0 && !loading && (
        <Alert variant="info">{t('products.noResults')}</Alert>
      )}

      {searchActive ? (
        <div>
          {visibleProducts.map((product) => (
            <div key={product.id}>
              {renderCompactProductCard(product)}
            </div>
          ))}
        </div>
      ) : (
        <Row
          xs={1}
          md={2}
          lg={3}
          className="g-4"
        >
          {visibleProducts.map((product) => (
            <Col key={product.id}>
              {renderProductCard(product)}
            </Col>
          ))}
        </Row>
      )}

      {shouldShowProducts && filteredProducts.length === 0 && !loading && (
        <Alert variant="info">{t('products.noProducts')}</Alert>
      )}

      <ProductMediaModal
        show={mediaModalState.show}
        onHide={closeMediaModal}
        mediaItems={mediaModalState.items}
        initialIndex={mediaModalState.index}
        title={mediaModalState.title}
        resolveMediaUrl={getMediaUrl}
        isVideo={isVideo}
      />
    </Container>
  );
};

export default Products;
